import { api } from "@/features/utils/fetch-server";
import type {
  BookingTargets,
  Calendar,
  CalendarEvent,
  CalendarEventStats,
  CreateEventRequest,
  UpdateEventRequest,
} from "../types";

export class CalendarAPI {
  // Calendars
  static async listCalendars(clientId: string) {
    const response = await api.get<{ calendars: Calendar[] }>(
      `/api/calendars/v1/${clientId}/calendars`,
    );
    return response.calendars;
  }

  static async getCalendar(clientId: string, calendarId: string) {
    const response = await api.get<{ calendar: Calendar }>(
      `/api/calendars/v1/${clientId}/calendars/${calendarId}`,
    );
    return response.calendar;
  }

  // Calendar Events
  static async listEvents(
    clientId: string,
    options?: {
      customer_id?: string;
      timeMin?: number;
      timeMax?: number;
      limit?: number;
      offset?: number;
    },
  ) {
    const params = new URLSearchParams();
    if (options?.customer_id) params.append("customer_id", options.customer_id);
    if (options?.timeMin) params.append("timeMin", options.timeMin.toString());
    if (options?.timeMax) params.append("timeMax", options.timeMax.toString());
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());

    const queryString = params.toString();
    const url = `/api/calendars/v1/${clientId}/events${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await api.get<{ events: CalendarEvent[] }>(url);
    return response.events;
  }

  static async getEvent(clientId: string, eventId: string) {
    const response = await api.get<{ event: CalendarEvent }>(
      `/api/calendars/v1/${clientId}/events/${eventId}`,
    );
    return response.event;
  }

  static async getBookingTargets(clientId: string) {
    return api.get<BookingTargets>(
      `/api/calendars/v1/${clientId}/booking-targets`,
    );
  }

  static async createEvent(clientId: string, data: CreateEventRequest) {
    const response = await api.post<
      { event: CalendarEvent },
      CreateEventRequest
    >(`/api/calendars/v1/${clientId}/events`, data);
    return response.event;
  }

  static async updateEvent(
    clientId: string,
    eventId: string,
    data: UpdateEventRequest,
  ) {
    const response = await api.put<
      { event: CalendarEvent },
      UpdateEventRequest
    >(`/api/calendars/v1/${clientId}/events/${eventId}`, data);
    return response.event;
  }

  static async deleteEvent(clientId: string, eventId: string): Promise<void> {
    await api.delete(`/api/calendars/v1/${clientId}/events/${eventId}`);
  }

  // Calendar Statistics
  static async getStats(clientId: string) {
    const response = await api.get<CalendarEventStats>(
      `/api/calendars/v1/${clientId}/stats`,
    );
    return response;
  }
}
